import { Rocket, Search, Building2, Zap, TrendingUp, Wrench, Brain, Cloud, Lock, Code, Database, Settings, Smartphone, BarChart3, Package, MessageCircle, RotateCw, Radio, Headset, Microscope, Link } from 'lucide-react';

export const serviceCategories = [
  {
    slug: 'product-engineering',
    tag: 'Engineering',
    title: 'Product Engineering',
    subtitle: 'Build scalable digital products faster with engineering teams that understand business outcomes, product strategy, and modern architecture.',
    icon: Rocket,
    accentColor: '#2563eb',
    accentGrad: 'linear-gradient(140deg, #1e40af 0%, #2563eb 60%, #3b82f6 100%)',
    features: ['Product Discovery', 'Architecture Design', 'MVP Build', 'Scaling & Optimization'],
    image: '/service-images/product-engineering.jpg',
    stack: ['.NET', 'Java', 'Python', 'Node.js', 'React', 'Angular', 'Flutter', 'React Native', 'AWS', 'Azure', 'GCP'],
    deliverables: ['Product workshops', 'System architecture', 'Rapid MVP development', 'Performance optimization', 'Infrastructure scaling'],
    description: 'Build scalable digital products faster with engineering teams that understand business outcomes, product strategy, and modern architecture. From MVP development to enterprise scale platforms, we help companies design, engineer, modernize, and optimize software products built for long term growth.\n\nWe combine product thinking, agile delivery, and scalable engineering to help startups and enterprises accelerate innovation without compromising performance or quality.',
    keyOutcomes: [
      'Faster product launches',
      'Reduced engineering complexity',
      'Scalable cloud native architecture',
      'Accelerated feature delivery',
      'Long term maintainability',
    ],
    bullets: [
      'Product discovery and requirement gathering with clear technical roadmaps',
      'Scalable architecture design optimized for performance, flexibility, and future growth',
      'Production ready MVP development to validate product market fit',
      'Performance optimization and scalability improvements for growing platforms',
      'Bug fixing, continuous improvements, and long term engineering support',
    ],
    subServices: [
      {
        title: 'Product Discovery',
        icon: Search,
        overview: 'Validate ideas, define product requirements, and create a clear technical roadmap before development begins.',
        offers: [
          'Product workshops',
          'Requirement gathering',
          'User journey mapping',
          'Feature prioritization',
          'Technical feasibility analysis',
          'Product roadmaps',
        ],
        capabilities: ['Strategic product analysis', 'Market validation', 'Technical feasibility assessment', 'Roadmap development'],
        outcomes: ['Validated product concepts', 'Clear technical direction', 'Reduced development risk', 'Aligned stakeholders'],
      },
      {
        title: 'Architecture Design',
        icon: Building2,
        overview: 'Design scalable and secure software architectures optimized for performance, flexibility, and future growth.',
        offers: [
          'System architecture',
          'Microservices design',
          'API architecture',
          'Database design',
          'Cloud native planning',
          'Scalability consulting',
        ],
        capabilities: ['Enterprise architecture design', 'Cloud native patterns', 'API first design', 'High availability systems'],
        outcomes: ['Scalable architecture', 'Reduced technical debt', 'Improved performance', 'Future proof design'],
      },
      {
        title: 'MVP Build',
        icon: Zap,
        overview: 'Launch production ready MVPs quickly to validate product market fit and accelerate investor or customer acquisition.',
        offers: [
          'Rapid MVP development',
          'Agile product delivery',
          'Cross platform applications',
          'Backend and frontend engineering',
          'QA and deployment',
          'Cloud infrastructure setup',
        ],
        capabilities: ['Agile development methodology', 'Full stack engineering', 'Rapid prototyping', 'Cloud deployment'],
        outcomes: ['Faster time to market', 'Validated market fit', 'Production ready systems', 'Investor ready product'],
      },
      {
        title: 'Scaling & Optimization',
        icon: TrendingUp,
        overview: 'Optimize performance, scalability, and reliability for growing platforms handling increasing traffic and operational complexity.',
        offers: [
          'Performance optimization',
          'Scalability improvements',
          'Database optimization',
          'Infrastructure scaling',
          'Cost optimization',
          'Platform modernization',
        ],
        capabilities: ['Performance tuning', 'Database optimization', 'Infrastructure automation', 'Cost efficiency'],
        outcomes: ['Improved performance', 'Increased scalability', 'Reduced costs', 'Enhanced reliability'],
      },
      {
        title: 'Maintenance & Support',
        icon: Wrench,
        overview: 'Ensure continuous platform reliability with proactive monitoring, updates, and long term engineering support.',
        offers: [
          'Bug fixing and maintenance',
          'Continuous improvements',
          'Security updates',
          'Infrastructure monitoring',
          'SLA based support',
          'Version upgrades',
        ],
        capabilities: ['24/7 monitoring', 'Incident response', 'Security management', 'System optimization'],
        outcomes: ['Maximum uptime', 'Security compliance', 'System reliability', 'Reduced downtime'],
      },
    ],
    caseStudies: [
      {
        id: 7,
        title: 'Telemedicine Platform Modernization',
        industry: 'Healthcare & Life Sciences',
        description: 'Modernized telemedicine platform for seamless patient-provider interactions with HIPAA compliance.',
        metrics: [
          { value: '50K+', label: 'Active Users' },
          { value: '99.9%', label: 'Uptime' },
          { value: '40%', label: 'Cost Reduction' },
        ],
      },
      {
        id: 8,
        title: 'AI-Assisted Diagnostics Workflow',
        industry: 'Healthcare & Life Sciences',
        description: 'Integrated AI-powered diagnostics system improving clinical efficiency and accuracy.',
        metrics: [
          { value: '35%', label: 'Faster Diagnosis' },
          { value: '92%', label: 'Accuracy Rate' },
          { value: '200+', label: 'Hospitals' },
        ],
      },
      {
        id: 9,
        title: 'Healthcare Analytics Dashboard',
        industry: 'Healthcare & Life Sciences',
        description: 'Built comprehensive analytics platform for real-time clinical and operational insights.',
        metrics: [
          { value: '1B+', label: 'Data Points' },
          { value: '45%', label: 'Faster Analysis' },
          { value: '24/7', label: 'Real-time Sync' },
        ],
      },
      {
        id: 10,
        title: 'Patient Engagement Mobile App',
        industry: 'Healthcare & Life Sciences',
        description: 'Cross-platform mobile application for patient engagement and appointment management.',
        metrics: [
          { value: '100K+', label: 'Downloads' },
          { value: '4.8/5', label: 'App Rating' },
          { value: '65%', label: 'Engagement' },
        ],
      },
    ],
    whitePapers: [
      'Scaling Digital Products with Cloud Native Architecture',
      'Building Investor Ready MVPs',
      'Enterprise Product Modernization Strategies',
      'Dedicated Engineering Teams for High Growth Startups',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Logistics', 'EdTech', 'Retail', 'SaaS'],
  },

  {
    slug: 'web-mobile-development',
    tag: 'Development',
    title: 'Web & Mobile Development',
    subtitle: 'Engineer modern web and mobile applications that deliver seamless digital experiences across devices and platforms.',
    icon: Smartphone,
    accentColor: '#16a34a',
    accentGrad: 'linear-gradient(140deg, #14532d 0%, #16a34a 60%, #22c55e 100%)',
    features: ['React Applications', 'React Native Apps', 'Progressive Web Apps', 'Enterprise Portals'],
    image: '/service-images/web-mobile-development.jpg',
    stack: ['React.js', 'Next.js', 'React Native', 'Angular', 'Vue.js', 'TypeScript', 'WebAssembly'],
    deliverables: ['Web applications', 'Mobile applications', 'Cross platform solutions', 'Progressive web apps'],
    description: 'Engineer modern web and mobile applications that deliver seamless digital experiences across devices and platforms. We build scalable applications using modern frameworks and cloud native technologies optimized for speed, usability, and performance.',
    keyOutcomes: [
      'High performance applications',
      'Mobile first user experiences',
      'Cross platform scalability',
      'Faster deployment cycles',
      'Enhanced user engagement',
    ],
    bullets: [
      'React and Next.js applications with server side rendering and SEO optimization',
      'React Native cross platform mobile development for iOS and Android',
      'Progressive web apps with offline support and push notifications',
      'Enterprise portals for operations, collaboration, and workflow management',
      'Modern UI engineering focused on usability, responsiveness, and performance',
    ],
    subServices: [
      {
        title: 'React and Next.js Applications',
        icon: Code,
        overview: 'Build lightning fast web applications with server side rendering, SEO optimization, and scalable frontend architecture.',
        offers: [
          'React.js development',
          'Next.js applications',
          'SSR and SSG',
          'API integrations',
          'Progressive rendering',
          'Enterprise dashboards',
        ],
        capabilities: ['Full stack web development', 'Server side rendering', 'Performance optimization', 'SEO optimization'],
        outcomes: ['Faster page loads', 'Better SEO rankings', 'Improved user engagement', 'Scalable web platforms'],
      },
      {
        title: 'React Native Applications',
        icon: Smartphone,
        overview: 'Develop cross platform mobile applications with native like performance for iOS and Android.',
        offers: [
          'Cross platform development',
          'Native integrations',
          'Real time functionality',
          'Offline first architecture',
          'App store deployment',
          'Mobile optimization',
        ],
        capabilities: ['iOS and Android development', 'Native module integration', 'Real time features', 'Cross platform consistency'],
        outcomes: ['Faster mobile development', 'Reduced development costs', 'Native performance', 'Wide platform coverage'],
      },
      {
        title: 'Progressive Web Apps',
        icon: Cloud,
        overview: 'Create installable web applications with app like performance and offline capabilities.',
        offers: [
          'Offline support',
          'Push notifications',
          'Fast loading architecture',
          'Mobile optimization',
          'Device compatibility',
          'PWA deployment',
        ],
        capabilities: ['Offline first design', 'Service workers', 'App shell architecture', 'Device API integration'],
        outcomes: ['App like experience', 'Offline functionality', 'Faster engagement', 'Reduced friction'],
      },
      {
        title: 'Enterprise Portals',
        icon: Building2,
        overview: 'Build secure and scalable internal platforms for operations, collaboration, and workflow management.',
        offers: [
          'Employee portals',
          'Admin dashboards',
          'Role based access',
          'Workflow systems',
          'Document management',
          'Internal communication platforms',
        ],
        capabilities: ['Enterprise security', 'Complex workflows', 'Role based access control', 'Data integration'],
        outcomes: ['Improved productivity', 'Better collaboration', 'Reduced operational friction', 'Enhanced visibility'],
      },
      {
        title: 'UI Engineering',
        icon: Code,
        overview: 'Design and develop modern interfaces focused on usability, responsiveness, and performance.',
        offers: [
          'Responsive UI development',
          'Design system implementation',
          'Accessibility optimization',
          'Component libraries',
          'Frontend architecture',
          'Interaction optimization',
        ],
        capabilities: ['Design system development', 'Accessibility compliance', 'Performance optimization', 'Component driven development'],
        outcomes: ['Beautiful interfaces', 'Accessible experiences', 'Reusable components', 'Improved user satisfaction'],
      },
    ],
    caseStudies: [
      {
        id: 11,
        title: 'Digital Banking Platform Engineering',
        industry: 'FinTech',
        description: 'Built secure, scalable digital banking platform with real-time payment processing.',
        metrics: [
          { value: '500K+', label: 'Users' },
          { value: '99.99%', label: 'Uptime' },
          { value: '10M+', label: 'Transactions' },
        ],
      },
      {
        id: 12,
        title: 'Payment Infrastructure Scaling',
        industry: 'FinTech',
        description: 'Engineered high-throughput payment gateway handling enterprise transaction volume.',
        metrics: [
          { value: '$2B+', label: 'Annual GMV' },
          { value: '50K+', label: 'TPS' },
          { value: '99.99%', label: 'Uptime SLA' },
        ],
      },
      {
        id: 13,
        title: 'Fraud Detection Analytics System',
        industry: 'FinTech',
        description: 'Developed ML-powered fraud detection reducing fraudulent transactions.',
        metrics: [
          { value: '99.5%', label: 'Detection Rate' },
          { value: '<50ms', label: 'Detection Time' },
          { value: '40%', label: 'False Positive Drop' },
        ],
      },
      {
        id: 14,
        title: 'Investment Platform Modernization',
        industry: 'FinTech',
        description: 'Modernized legacy investment platform with cloud-native architecture.',
        metrics: [
          { value: '$50B+', label: 'AUM' },
          { value: '100K+', label: 'Users' },
          { value: '35%', label: 'Cost Reduction' },
        ],
      },
    ],
    whitePapers: [
      'Building High Performance Web Applications',
      'Cross Platform Mobile Engineering Strategies',
      'Progressive Web Apps for Enterprise Operations',
      'Frontend Scalability and UI Engineering Best Practices',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Retail', 'EdTech', 'Logistics', 'SaaS'],
  },

  {
    slug: 'ai-automation',
    tag: 'AI',
    title: 'AI & Intelligent Automation',
    subtitle: 'Leverage AI powered systems and intelligent automation to streamline operations, improve decision making, and create next generation digital experiences.',
    icon: Brain,
    accentColor: '#e11d48',
    accentGrad: 'linear-gradient(140deg, #881337 0%, #e11d48 60%, #fb7185 100%)',
    features: ['AI Assistants', 'RAG Systems', 'LLM Integrations', 'AI Automation'],
    image: '/service-images/ai-automation.jpg',
    stack: ['OpenAI', 'Claude', 'Gemini', 'LangChain', 'Vector Databases', 'Python', 'Node.js'],
    deliverables: ['AI assistants', 'RAG systems', 'LLM integrations', 'Workflow automation'],
    description: 'Leverage AI powered systems and intelligent automation to streamline operations, improve decision making, and create next generation digital experiences. We help businesses integrate AI into products, workflows, and enterprise operations using modern LLM and automation frameworks.',
    keyOutcomes: [
      'Automated workflows',
      'AI driven productivity',
      'Faster decision making',
      'Reduced operational costs',
      'Intelligent digital experiences',
    ],
    bullets: [
      'AI powered assistants that automate communication, support, and operational workflows',
      'Retrieval Augmented Generation systems powered by enterprise data and knowledge bases',
      'Large language model integrations for productivity and automation',
      'AI automation for repetitive workflows and operational processes',
      'Internal AI tools and platforms tailored to organizational needs',
    ],
    subServices: [
      {
        title: 'AI Assistants',
        icon: MessageCircle,
        overview: 'Develop AI powered assistants that automate communication, support, and operational workflows.',
        offers: [
          'Conversational AI',
          'Virtual assistants',
          'Customer support automation',
          'Internal productivity assistants',
          'Voice and chat interfaces',
          'AI copilots',
        ],
        capabilities: ['Natural language processing', 'Conversational interfaces', 'Task automation', 'Context awareness'],
        outcomes: ['24/7 support availability', 'Reduced support costs', 'Improved response times', 'Enhanced user satisfaction'],
      },
      {
        title: 'RAG Systems',
        icon: Search,
        overview: 'Build Retrieval Augmented Generation systems powered by enterprise data and knowledge bases.',
        offers: [
          'Knowledge retrieval',
          'Enterprise search',
          'AI document assistants',
          'Semantic search',
          'Context aware AI',
          'Vector database integrations',
        ],
        capabilities: ['Knowledge base indexing', 'Semantic search', 'Context retrieval', 'Intelligent ranking'],
        outcomes: ['Better information access', 'Faster knowledge discovery', 'Improved decision making', 'Reduced search time'],
      },
      {
        title: 'LLM Integrations',
        icon: Code,
        overview: 'Integrate large language models into applications and enterprise workflows securely and efficiently.',
        offers: [
          'OpenAI integrations',
          'Claude integrations',
          'Gemini integrations',
          'Prompt engineering',
          'AI workflow orchestration',
          'Secure AI deployment',
        ],
        capabilities: ['API integration', 'Prompt optimization', 'Token management', 'Secure deployment'],
        outcomes: ['Faster AI implementation', 'Improved AI quality', 'Cost optimization', 'Enterprise security'],
      },
      {
        title: 'AI Automation',
        icon: Settings,
        overview: 'Automate repetitive workflows and operational processes using AI driven systems.',
        offers: [
          'Workflow automation',
          'AI based data processing',
          'Intelligent task routing',
          'Process optimization',
          'Automation dashboards',
          'AI monitoring systems',
        ],
        capabilities: ['Intelligent automation', 'Process mining', 'Optimization algorithms', 'Real time monitoring'],
        outcomes: ['Reduced manual effort', 'Process efficiency', 'Error reduction', 'Cost savings'],
      },
      {
        title: 'Internal AI Tools',
        icon: Wrench,
        overview: 'Build custom AI platforms tailored to organizational processes and internal operations.',
        offers: [
          'Internal copilots',
          'AI knowledge hubs',
          'Reporting assistants',
          'Workflow engines',
          'AI enabled CRMs',
          'AI productivity systems',
        ],
        capabilities: ['Custom AI solutions', 'Internal tool development', 'Process integration', 'User specific customization'],
        outcomes: ['Improved productivity', 'Better collaboration', 'Faster decisions', 'Competitive advantage'],
      },
    ],
    caseStudies: [
      {
        id: 15,
        title: 'Fleet Management Optimization Platform',
        industry: 'Logistics',
        description: 'Developed AI-powered fleet management system optimizing routes and fuel efficiency.',
        metrics: [
          { value: '22%', label: 'Cost Reduction' },
          { value: '500+', label: 'Vehicles Managed' },
          { value: '98%', label: 'On-Time Rate' },
        ],
      },
      {
        id: 16,
        title: 'Shipment Tracking System',
        industry: 'Logistics',
        description: 'Built real-time shipment tracking platform with end-to-end visibility.',
        metrics: [
          { value: '1M+', label: 'Daily Shipments' },
          { value: '<5min', label: 'Update Latency' },
          { value: '99.9%', label: 'Accuracy' },
        ],
      },
      {
        id: 17,
        title: 'Warehouse Analytics Dashboard',
        industry: 'Logistics',
        description: 'Created comprehensive warehouse analytics for inventory and operations optimization.',
        metrics: [
          { value: '40%', label: 'Efficiency Gain' },
          { value: '100+', label: 'Warehouses' },
          { value: '24/7', label: 'Real-time Monitoring' },
        ],
      },
      {
        id: 18,
        title: 'AI Route Optimization Engine',
        industry: 'Logistics',
        description: 'Implemented ML-based route optimization reducing delivery times and costs.',
        metrics: [
          { value: '30%', label: 'Faster Delivery' },
          { value: '25%', label: 'Fuel Savings' },
          { value: '50K+', label: 'Routes Daily' },
        ],
      },
    ],
    whitePapers: [
      'Enterprise AI Adoption Strategies',
      'RAG Systems for Organizational Knowledge',
      'Intelligent Workflow Automation',
      'Secure LLM Implementation Frameworks',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Logistics', 'EdTech', 'Retail', 'SaaS'],
  },

  {
    slug: 'cloud-devops-infrastructure',
    tag: 'Infrastructure',
    title: 'Cloud, DevOps & Infrastructure',
    subtitle: 'Modernize infrastructure and accelerate software delivery with cloud native DevOps and scalable infrastructure engineering.',
    icon: Cloud,
    accentColor: '#0891b2',
    accentGrad: 'linear-gradient(140deg, #0e7490 0%, #0891b2 60%, #06b6d4 100%)',
    features: ['Cloud Strategy', 'DevOps Pipelines', 'Containerization', 'Monitoring & Reliability'],
    image: '/service-images/cloud-devops-infrastructure.jpg',
    stack: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Terraform', 'Docker', 'Jenkins', 'Prometheus'],
    deliverables: ['Cloud architecture', 'CI/CD pipelines', 'Infrastructure as Code', 'Observability systems'],
    description: 'Modernize infrastructure and accelerate software delivery with cloud native DevOps and scalable infrastructure engineering. We help organizations improve reliability, security, deployment speed, and operational efficiency across modern cloud environments.',
    keyOutcomes: [
      'Faster deployment cycles',
      'Improved platform reliability',
      'Scalable cloud infrastructure',
      'Enhanced system security',
      'Reduced operational overhead',
    ],
    bullets: [
      'AWS, Azure, and GCP cloud architecture design and multi cloud deployment',
      'CI/CD automation with Jenkins, GitHub Actions, and GitLab CI',
      'Docker containerization and Kubernetes orchestration for scalable deployment',
      'Infrastructure as Code with Terraform for reproducible environments',
      'Monitoring, observability, and reliability engineering for production systems',
      'Security and compliance frameworks for enterprise grade infrastructure',
    ],
    subServices: [
      {
        title: 'AWS / Azure / GCP',
        icon: Cloud,
        overview: 'Design and manage scalable multi cloud infrastructure optimized for performance and cost efficiency.',
        offers: [
          'Cloud architecture',
          'Infrastructure migration',
          'Multi cloud deployment',
          'Cloud optimization',
          'Disaster recovery',
          'Infrastructure automation',
        ],
        capabilities: ['Multi cloud expertise', 'Architecture optimization', 'Cost management', 'Compliance readiness'],
        outcomes: ['Optimized cloud costs', 'Improved performance', 'Global scalability', 'Enterprise security'],
      },
      {
        title: 'DevOps Pipelines',
        icon: RotateCw,
        overview: 'Implement CI/CD pipelines that accelerate software delivery and improve deployment reliability.',
        offers: [
          'CI/CD automation',
          'Release management',
          'GitOps workflows',
          'Deployment automation',
          'Build optimization',
          'DevOps consulting',
        ],
        capabilities: ['Automated testing', 'Continuous deployment', 'Pipeline orchestration', 'Deployment safety'],
        outcomes: ['Faster releases', 'Reduced failures', 'Improved reliability', 'Higher deployment frequency'],
      },
      {
        title: 'Containerization',
        icon: Package,
        overview: 'Deploy scalable applications using modern container orchestration and Kubernetes environments.',
        offers: [
          'Docker containerization',
          'Kubernetes orchestration',
          'Microservices deployment',
          'Container optimization',
          'Service mesh implementation',
          'Cluster management',
        ],
        capabilities: ['Container best practices', 'Kubernetes expertise', 'Microservices patterns', 'Container security'],
        outcomes: ['Scalable deployments', 'Resource efficiency', 'Consistent environments', 'Easy scaling'],
      },
      {
        title: 'Monitoring & Reliability',
        icon: BarChart3,
        overview: 'Ensure infrastructure stability and uptime with proactive monitoring and reliability engineering.',
        offers: [
          'Observability systems',
          'Real time monitoring',
          'Incident management',
          'Performance tracking',
          'Log management',
          'Reliability engineering',
        ],
        capabilities: ['APM tools', 'Log aggregation', 'Alerting systems', 'Incident response'],
        outcomes: ['Improved uptime', 'Faster incident response', 'Better visibility', 'Proactive management'],
      },
      {
        title: 'Security & Compliance',
        icon: Lock,
        overview: 'Protect applications and infrastructure with enterprise grade security and compliance frameworks.',
        offers: [
          'Infrastructure security',
          'IAM implementation',
          'Compliance readiness',
          'Security monitoring',
          'Vulnerability management',
          'DevSecOps practices',
        ],
        capabilities: ['Security architecture', 'Compliance automation', 'Threat detection', 'Identity management'],
        outcomes: ['Reduced security risks', 'Compliance ready systems', 'Secure infrastructure', 'Risk mitigation'],
      },
    ],
    caseStudies: [
      {
        id: 19,
        title: 'SaaS Learning Management Platform',
        industry: 'EdTech',
        description: 'Built scalable cloud-native LMS supporting thousands of concurrent users.',
        metrics: [
          { value: '500K+', label: 'Users' },
          { value: '99.99%', label: 'Uptime' },
          { value: '10K+', label: 'Courses' },
        ],
      },
      {
        id: 20,
        title: 'Virtual Classroom Infrastructure',
        industry: 'EdTech',
        description: 'Engineered robust infrastructure for live virtual classes with HD video and low latency.',
        metrics: [
          { value: '100K+', label: 'Concurrent Users' },
          { value: '<100ms', label: 'Latency' },
          { value: '1080p', label: 'Video Quality' },
        ],
      },
      {
        id: 21,
        title: 'Student Analytics Dashboard',
        industry: 'EdTech',
        description: 'Created comprehensive analytics platform tracking learning outcomes and engagement.',
        metrics: [
          { value: '85%', label: 'Completion Rate' },
          { value: '1B+', label: 'Data Points' },
          { value: '45%', label: 'Engagement Lift' },
        ],
      },
      {
        id: 22,
        title: 'Mobile-First Education Application',
        industry: 'EdTech',
        description: 'Developed cross-platform mobile app for seamless learning on-the-go.',
        metrics: [
          { value: '1M+', label: 'Downloads' },
          { value: '4.9/5', label: 'App Rating' },
          { value: '70%', label: 'Daily Active' },
        ],
      },
    ],
    whitePapers: [
      'Cloud Modernization Frameworks',
      'Kubernetes Adoption Strategies',
      'DevOps Automation Best Practices',
      'Infrastructure Security and Compliance',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Logistics', 'EdTech', 'Retail', 'SaaS'],
  },

  {
    slug: 'data-analytics',
    tag: 'Data',
    title: 'Data & Analytics',
    subtitle: 'Transform raw data into actionable business intelligence with scalable data engineering and analytics platforms.',
    icon: BarChart3,
    accentColor: '#9333ea',
    accentGrad: 'linear-gradient(140deg, #581c87 0%, #9333ea 60%, #a855f7 100%)',
    features: ['ETL Pipelines', 'Dashboards & Reporting', 'Data Warehousing', 'Real Time Analytics'],
    image: '/service-images/data-analytics.jpg',
    stack: ['Python', 'SQL', 'Power BI', 'Tableau', 'Snowflake', 'Databricks', 'Apache Spark', 'Kafka'],
    deliverables: ['ETL pipelines', 'Data warehouses', 'Analytics dashboards', 'Predictive models'],
    description: 'Transform raw data into actionable business intelligence with scalable data engineering and analytics platforms. We help organizations centralize, process, analyze, and visualize data for faster and smarter decision making.',
    keyOutcomes: [
      'Centralized data visibility',
      'Real time business insights',
      'Improved reporting accuracy',
      'Scalable data infrastructure',
      'Data driven decision making',
    ],
    bullets: [
      'ETL pipeline development for reliable data ingestion and transformation',
      'Data warehouse and data lake architecture on cloud platforms',
      'Interactive dashboards and reporting systems for operational and executive visibility',
      'Real time analytics for high volume operational environments',
      'Predictive modeling and recommendation engines for smarter decisions',
      'Data governance and quality management frameworks',
    ],
    subServices: [
      {
        title: 'ETL Pipelines',
        icon: Wrench,
        overview: 'Build reliable pipelines for extracting, transforming, and processing structured and unstructured data.',
        offers: [
          'Data ingestion',
          'ETL/ELT workflows',
          'API integrations',
          'Data transformation',
          'Data quality management',
          'Workflow orchestration',
        ],
        capabilities: ['Batch and streaming', 'Data validation', 'Error handling', 'Scalable processing'],
        outcomes: ['Reliable data flow', 'Data quality assurance', 'Faster processing', 'Automated pipelines'],
      },
      {
        title: 'Dashboards & Reporting',
        icon: TrendingUp,
        overview: 'Create interactive dashboards and reporting systems for operational and executive visibility.',
        offers: [
          'BI dashboards',
          'KPI reporting',
          'Executive analytics',
          'Real time visualization',
          'Custom reporting systems',
          'Data storytelling',
        ],
        capabilities: ['Interactive visualizations', 'Real time dashboards', 'Self service analytics', 'Data integration'],
        outcomes: ['Better insights', 'Faster decisions', 'Improved visibility', 'Data driven culture'],
      },
      {
        title: 'Data Warehousing',
        icon: Database,
        overview: 'Design scalable data warehouses optimized for analytics and enterprise reporting.',
        offers: [
          'Data lake architecture',
          'Warehousing solutions',
          'Big data processing',
          'Data modeling',
          'Query optimization',
          'Centralized analytics',
        ],
        capabilities: ['Cloud data platforms', 'Dimensional modeling', 'Performance optimization', 'Metadata management'],
        outcomes: ['Scalable infrastructure', 'Fast queries', 'Centralized insights', 'Cost efficiency'],
      },
      {
        title: 'Real Time Analytics',
        icon: Zap,
        overview: 'Enable real time monitoring and analytics for high volume operational environments.',
        offers: [
          'Streaming analytics',
          'Real time dashboards',
          'Event processing',
          'Predictive analytics',
          'Operational intelligence',
          'AI driven analytics',
        ],
        capabilities: ['Event streaming', 'Low latency processing', 'Real time visualization', 'Pattern detection'],
        outcomes: ['Instant insights', 'Proactive decisions', 'Competitive advantage', 'Operational excellence'],
      },
    ],
    caseStudies: [
      {
        id: 23,
        title: 'Omnichannel Retail Platform',
        industry: 'Retail',
        description: 'Built unified omnichannel platform integrating online and physical retail operations.',
        metrics: [
          { value: '28%', label: 'Conversion Lift' },
          { value: '2M+', label: 'Monthly Visitors' },
          { value: '$100M+', label: 'Annual GMV' },
        ],
      },
      {
        id: 24,
        title: 'AI Recommendation Engine',
        industry: 'Retail',
        description: 'Developed ML-powered recommendation system increasing product discovery and sales.',
        metrics: [
          { value: '35%', label: 'AOV Increase' },
          { value: '1B+', label: 'Recommendations Daily' },
          { value: '4.7/5', label: 'User Rating' },
        ],
      },
      {
        id: 25,
        title: 'Retail Inventory Analytics',
        industry: 'Retail',
        description: 'Created real-time inventory analytics optimizing stock levels across locations.',
        metrics: [
          { value: '20%', label: 'Stock Optimization' },
          { value: '500+', label: 'Locations' },
          { value: '<1min', label: 'Sync Latency' },
        ],
      },
      {
        id: 26,
        title: 'Mobile Commerce Application',
        industry: 'Retail',
        description: 'Engineered high-performance mobile app for seamless shopping experience.',
        metrics: [
          { value: '5M+', label: 'Downloads' },
          { value: '4.8/5', label: 'App Rating' },
          { value: '45%', label: 'Mobile Revenue' },
        ],
      },
    ],
    whitePapers: [
      'Building Modern Analytics Platforms',
      'Real Time Operational Intelligence',
      'Data Engineering Best Practices',
      'Enterprise BI Transformation',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Logistics', 'EdTech', 'Retail', 'SaaS'],
  },

  {
    slug: 'emerging-technologies',
    tag: 'Innovation',
    title: 'Emerging Technologies',
    subtitle: 'Explore next generation technologies that unlock innovation, immersive experiences, and new business models.',
    icon: Rocket,
    accentColor: '#d97706',
    accentGrad: 'linear-gradient(140deg, #78350f 0%, #d97706 60%, #f59e0b 100%)',
    features: ['Blockchain Solutions', 'IoT Solutions', 'AR/VR Experiences', 'Innovation Labs'],
    image: '/service-images/emerging-technologies.jpg',
    stack: ['Solidity', 'Ethereum', 'IoT Sensors', 'AR/VR Frameworks', 'Unity', 'Unreal Engine'],
    deliverables: ['Blockchain applications', 'IoT systems', 'Immersive experiences', 'Prototypes'],
    description: 'Explore next generation technologies that unlock innovation, immersive experiences, and new business models. Our emerging technology solutions help organizations experiment, prototype, and scale cutting edge digital capabilities.',
    keyOutcomes: [
      'Next generation product capabilities',
      'Immersive user experiences',
      'New revenue models',
      'Competitive differentiation',
      'Future ready systems',
    ],
    bullets: [
      'Blockchain enabled decentralized applications and platforms for transparency',
      'IoT solutions for connected systems and real time device data',
      'AR/VR experiences for training, visualization, and engagement',
      'Innovation labs for prototyping and validating experimental technologies',
    ],
    subServices: [
      {
        title: 'Blockchain Solutions',
        icon: Link,
        overview: 'Build decentralized applications and blockchain powered platforms for transparency and security.',
        offers: [
          'Smart contract development',
          'Blockchain architecture',
          'Decentralized applications',
          'Token engineering',
          'Security audits',
          'Compliance frameworks',
        ],
        capabilities: ['Smart contracts', 'Consensus mechanisms', 'Decentralized systems', 'Token economics'],
        outcomes: ['Transparency', 'Security', 'Automation', 'Trust'],
      },
      {
        title: 'IoT Solutions',
        icon: Radio,
        overview: 'Develop connected systems that collect, process, and analyze real time device data.',
        offers: [
          'Device integration',
          'IoT platforms',
          'Sensor networks',
          'Edge computing',
          'Data pipelines',
          'Predictive maintenance',
        ],
        capabilities: ['Device connectivity', 'Data collection', 'Edge processing', 'Cloud integration'],
        outcomes: ['Connected infrastructure', 'Real time insights', 'Predictive maintenance', 'Operational efficiency'],
      },
      {
        title: 'AR/VR Experiences',
        icon: Headset,
        overview: 'Create immersive digital experiences for training, visualization, and engagement.',
        offers: [
          'AR applications',
          'VR training simulations',
          'Mixed reality experiences',
          '3D visualization',
          'Interactive content',
          'Enterprise deployment',
        ],
        capabilities: ['3D development', 'Immersive design', 'User interaction', 'Cross platform deployment'],
        outcomes: ['Enhanced engagement', 'Effective training', 'Immersive experiences', 'Competitive advantage'],
      },
      {
        title: 'Innovation Labs',
        icon: Microscope,
        overview: 'Prototype and validate experimental technologies through rapid innovation cycles.',
        offers: [
          'Technology exploration',
          'Rapid prototyping',
          'Proof of concept development',
          'Feasibility studies',
          'MVP validation',
          'Scaling strategies',
        ],
        capabilities: ['Emerging tech expertise', 'Rapid iteration', 'Risk mitigation', 'Commercialization'],
        outcomes: ['Innovation validation', 'Competitive advantage', 'New revenue streams', 'Market leadership'],
      },
    ],
    caseStudies: [
      {
        id: 27,
        title: 'Multi-Tenant SaaS Engineering',
        industry: 'SaaS',
        description: 'Engineered scalable multi-tenant architecture supporting global enterprise clients.',
        metrics: [
          { value: '500+', label: 'Enterprise Clients' },
          { value: '99.99%', label: 'Uptime SLA' },
          { value: '10M+', label: 'Daily Users' },
        ],
      },
      {
        id: 28,
        title: 'SaaS Platform Modernization',
        industry: 'SaaS',
        description: 'Modernized legacy SaaS platform with microservices and cloud-native architecture.',
        metrics: [
          { value: '65%', label: 'Cost Reduction' },
          { value: '5x', label: 'Faster Deployments' },
          { value: '99.99%', label: 'Uptime' },
        ],
      },
      {
        id: 29,
        title: 'Subscription Analytics Platform',
        industry: 'SaaS',
        description: 'Built comprehensive analytics platform for subscription metrics and insights.',
        metrics: [
          { value: '200+', label: 'SaaS Clients' },
          { value: '1B+', label: 'Events/Month' },
          { value: '<50ms', label: 'Query Latency' },
        ],
      },
      {
        id: 30,
        title: 'Cloud-Native SaaS Scalability',
        industry: 'SaaS',
        description: 'Implemented auto-scaling infrastructure handling 10x growth with zero downtime.',
        metrics: [
          { value: '10x', label: 'Scale Achieved' },
          { value: '99.99%', label: 'Uptime' },
          { value: '40%', label: 'Cost Savings' },
        ],
      },
    ],
    whitePapers: [
      'Blockchain Adoption Strategies',
      'IoT Infrastructure Engineering',
      'Immersive AR/VR Enterprise Applications',
      'Innovation Led Digital Transformation',
    ],
    industries: ['FinTech', 'Healthcare and Life Sciences', 'Logistics', 'Manufacturing', 'Retail'],
  },
];

export function getServiceCategory(slug) {
  return serviceCategories.find((c) => c.slug === slug);
}
